import {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {getUsers} from 'src/openapi/admin/apis/UserIdentityApi';
import {searchUsers} from 'src/openapi/admin/apis/UserSearchApi';
import {useAppDispatch, useAppSelector} from 'src/store';

import {
    accessManagementUsersIsLoadingSelector,
    accessManagementUsersItemsSelector,
    accessManagementUsersPaginationSelector,
    accessManagementUsersSearchCriteriaSelector,
    accessManagementUsersSortCriteriaSelector,
    setLoading,
    setUsersData,
} from '../service/access-management-users.slice';

import type {User} from 'src/openapi/admin/models/User';

type Return = {users: User[], loading: boolean};

export const useAccessManagementData = (): Return => {
    const apiDispatch = useAppDispatch();
    const reduxDispatch = useDispatch();
    const searchCriteria = useAppSelector(accessManagementUsersSearchCriteriaSelector);
    const sortCriteria = useAppSelector(accessManagementUsersSortCriteriaSelector);
    const pagination = useAppSelector(accessManagementUsersPaginationSelector);
    const users = useAppSelector(accessManagementUsersItemsSelector);
    const loading = useAppSelector(accessManagementUsersIsLoadingSelector);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const reqId = ++requestIdRef.current;
        let isMounted = true;

        const isCurrentRequest = () => isMounted && reqId === requestIdRef.current;

        const fetchUserIds = async () => {
            const response = await apiDispatch(searchUsers({
                userSearchRequest: {
                    searchCriteria,
                    pagination,
                    sortCriteria,
                },
            }));

            if (!isCurrentRequest() || !response.payload?.success) {
                return null;
            }

            const searchData = response.payload.data;
            return {
                userIds: searchData?.users ?? [],
                totalCount: searchData?.totalCount ?? 0,
            };
        };

        const fetchUserDetails = async (userIds: number[]) => {
            const response = await apiDispatch(getUsers({requestBody: userIds}));

            if (!isCurrentRequest() || !response.payload?.success) {
                return null;
            }

            const allUsers = response.payload.data ?? [];
            const userMap = new Map(allUsers.map(u => [u.userId, u]));
            return userIds.map(id => userMap.get(id)).filter(Boolean) as User[];
        };

        const loadData = async () => {
            reduxDispatch(setLoading(true));

            try {
                const searchResult = await fetchUserIds();

                if (!searchResult) {
                    if (isCurrentRequest()) {
                        reduxDispatch(setUsersData({users: [], totalCount: 0, loading: false}));
                    }
                    return;
                }

                const {userIds, totalCount} = searchResult;

                if (!userIds.length) {
                    reduxDispatch(setUsersData({users: [], totalCount, loading: false}));
                    return;
                }

                const fetchedUsers = await fetchUserDetails(userIds);

                if (!fetchedUsers) {
                    if (isCurrentRequest()) {
                        reduxDispatch(setUsersData({users: [], totalCount: 0, loading: false}));
                    }
                    return;
                }

                reduxDispatch(setUsersData({users: fetchedUsers, totalCount, loading: false}));
            } catch {
                if (isCurrentRequest()) {
                    reduxDispatch(setUsersData({users: [], totalCount: 0, loading: false}));
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [searchCriteria, sortCriteria, pagination, apiDispatch, reduxDispatch]);

    return {users, loading};
};
